import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FrameworkLogo from "./FrameworkLogo";

// Overview: the frameworks a buyer asks about first, one tile each, with the
// status in words. A framework with linked reports shows a small link icon
// (like the admin's): one report opens on the Resources tab; several open a
// short menu to pick one. A framework without a report is not a link.
// `resources`: the page's resources, for the reports' names.
const FrameworkStrip = ({ frameworks = [], resources = [], onOpenReport }) => {
  const theme = useTheme();
  const t = theme.trust;
  // The tile whose reports menu is open: { anchor, reports }.
  const [menu, setMenu] = useState(null);
  if (frameworks.length === 0) return null;
  const reportsOf = (f) =>
    (f.resourceIds || [])
      .map((id) => resources.find((r) => r.id === id))
      .filter(Boolean);

  return (
    <Box component="section" aria-labelledby="overview-compliance">
      <Typography
        id="overview-compliance"
        component="h2"
        sx={{
          fontSize: 17,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: t.ink,
          mb: 1.5,
        }}
      >
        Compliance
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
        }}
      >
        {frameworks.map((f) => {
          const compliant = f.status === "compliant";
          const reports = reportsOf(f);
          const report = reports.length > 0;
          return (
            <ButtonBase
              key={f.id}
              component={report ? "button" : "div"}
              disabled={!report}
              onClick={
                !report
                  ? undefined
                  : reports.length === 1
                    ? () => onOpenReport(reports[0].id)
                    : (e) => setMenu({ anchor: e.currentTarget, reports })
              }
              aria-haspopup={reports.length > 1 ? "menu" : undefined}
              aria-label={
                report
                  ? `${f.name}, ${compliant ? "compliant" : "in progress"}. ${
                      reports.length === 1
                        ? "View report"
                        : `${reports.length} reports`
                    }`
                  : undefined
              }
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 1.75,
                p: 2,
                textAlign: "left",
                borderRadius: `${t.radius.container}px`,
                border: `1px solid ${t.line}`,
                backgroundColor: t.surface,
                transition: `border-color 180ms ${t.ease}, box-shadow 180ms ${t.ease}`,
                "&:hover": report
                  ? {
                      borderColor: t.lineStrong,
                      boxShadow: "0 8px 24px -16px rgba(18, 26, 58, 0.35)",
                    }
                  : {},
                // Not a link: no hover, and it reads as plain text.
                "&.Mui-disabled": { cursor: "default", pointerEvents: "none" },
                "&:focus-visible": {
                  outline: `2px solid ${t.accent}`,
                  outlineOffset: 2,
                },
              }}
            >
              <FrameworkLogo framework={f} size={44} alt="" />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  title={f.name}
                  sx={{ fontSize: 15, fontWeight: 500, color: t.inkName }}
                >
                  {f.name}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mt: 0.25 }}
                >
                  {compliant ? (
                    <CheckCircleRoundedIcon
                      aria-hidden="true"
                      sx={{ fontSize: 15, color: t.verified }}
                    />
                  ) : (
                    <ScheduleRoundedIcon
                      aria-hidden="true"
                      sx={{ fontSize: 15, color: t.muted }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: compliant ? t.verified : t.muted,
                    }}
                  >
                    {compliant ? "Compliant" : "In progress"}
                  </Typography>
                </Stack>
              </Box>
              {report && (
                <Box
                  aria-hidden="true"
                  sx={{
                    ml: "auto",
                    alignSelf: "flex-start",
                    display: "grid",
                    placeItems: "center",
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: `1px solid ${t.line}`,
                    color: t.muted,
                  }}
                >
                  <LinkRoundedIcon sx={{ fontSize: 17 }} />
                </Box>
              )}
            </ButtonBase>
          );
        })}
      </Box>
      <Menu
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {(menu?.reports || []).map((r) => (
          <MenuItem
            key={r.id}
            onClick={() => {
              setMenu(null);
              onOpenReport(r.id);
            }}
            sx={{ fontSize: 14, gap: 1.25 }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 18, color: t.muted }} />
            {r.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default FrameworkStrip;
